import React from 'react';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'motion/react';
import * as Headless from '@headlessui/react';
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  CheckIcon,
  CircleStackIcon,
  DocumentTextIcon,
  TableCellsIcon
} from '@heroicons/react/24/outline';
import { Spinner } from '@oysterjs/ui/spinner';
import { AnimateIn } from '@oysterjs/ui/transition';
import { remark } from 'remark';
import stripMarkdown from 'strip-markdown';

import {
  AnimatedSanitizedMarkdown,
  MarkdownContainer
} from '@oysterjs/ais/components/sanitized-markdown';
import {
  CopyIcon,
  InfoIcon,
  PdfIcon,
  ThumbsDownIcon,
  ThumbsUpIcon
} from '@oysterjs/ais/components/icons';
import { useCanViewSettingsUsageTab } from '@oysterjs/ais/components/use-settings-usage-tab-enabled';
import { FeedbackDialog, useFeedbackDialog } from '@oysterjs/ais/components/feedback-dialog';
import {
  submitMessageRating,
  type SubmitMessageRatingInput
} from '@oysterjs/ais/api/data/feedback/mutations';
import {
  CaraWorkspaceMessageExportFormat,
  type CaraWorkspaceFieldsFragment,
  type CaraWorkspaceMessageFollowUpInput,
  type HtmlDocumentTemplateFieldsFragment,
  MessageFeedbackNegativeReason,
  MessageRating
} from '@oysterjs/ais/api/graphql';
import { formatUsageUnits } from '@oysterjs/ais/lib/usage';

import { useExportCaraWorkspaceMessage } from '@oysterjs/ais/components/workspaces/hooks/use-export-cara-workspace-message';
import { ResponseFollowUpOptions } from './conversation-response-follow-up';
import { AmsReconciliationModal } from '@oysterjs/ais/components/workspaces/ams-reconciliation/ams-reconciliation-modal';

export const ResponseMessage = (props: {
  htmlDocumentTemplates: HtmlDocumentTemplateFieldsFragment[];
  workspaceId: string;
  message: CaraWorkspaceFieldsFragment['messages'][0];
  onSelectFollowUp: (followUp: CaraWorkspaceMessageFollowUpInput) => void;
  usedFollowUpIds: Set<string>;
  platformUnitsUsed?: number | null;
  loading?: boolean;
  isLastItem?: boolean;
  readOnly?: boolean;
  /**
   * True while this message is still receiving tokens. Citation resolution is deferred
   * until streaming ends so a partial footnote URL cannot become a clickable destination.
   */
  streaming?: boolean;
  /** Resolved above the thread, so the Send menu knows its options before it opens. */
  amsReconciliationEnabled?: boolean;
}) => {
  const isSubstantialMessage = props.message.content.length > 500;

  return (
    <AnimateIn data-conversation-role="agent-message" data-message-id={props.message.id}>
      <div className="w-full relative self-start">
        <AnimatePresence mode="wait">
          {!!props.message.content && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              transition={{
                height: { duration: 0.15, ease: 'anticipate' },
                opacity: { duration: 0.3, ease: 'anticipate' }
              }}
              exit={{ height: 0, opacity: 0 }}
            >
              {/* citation-footnotes styles the inline citation markers as pill badges. */}
              <MarkdownContainer className="citation-footnotes cara-report-nav-container">
                <AnimatedSanitizedMarkdown
                  content={props.message.content}
                  resolveCitations={!props.streaming}
                />
              </MarkdownContainer>
              {props.loading && (
                <div className="flex flex-row justify-start mt-1.5">
                  <Spinner size="size-4" color="neutral" />
                </div>
              )}
            </motion.div>
          )}
          {!props.message.content && (
            <motion.div
              className="flex-1 truncate"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              transition={{
                height: { duration: 0.15, ease: 'anticipate' },
                opacity: { duration: 0.3, ease: 'anticipate' }
              }}
              exit={{ height: 0, opacity: 0 }}
            >
              <div className="h-3 mt-0 w-full bg-neutral-300 rounded-lg dark:bg-neutral-700 animate-pulse" />
              <div className="h-3 mt-1.5 w-3/4 bg-neutral-300 rounded-lg dark:bg-neutral-700 animate-pulse" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {isSubstantialMessage && !props.loading && !props.readOnly && (
        <MessageActions
          message={props.message}
          workspaceId={props.workspaceId}
          platformUnitsUsed={props.platformUnitsUsed}
          amsReconciliationEnabled={props.amsReconciliationEnabled}
        />
      )}
      {props.isLastItem && !props.readOnly && (
        <ResponseFollowUpOptions
          message={props.message}
          onSelectFollowUp={props.onSelectFollowUp}
          usedFollowUpIds={props.usedFollowUpIds}
        />
      )}
    </AnimateIn>
  );
};

/**
 * Tailwind classes shared by the message action menus. Mirrors
 * ATTACHMENT_PICKER_MENU_CLASS in cara-input-attachment-button.tsx so the compose
 * "+" menu and these menus stay visually symmetrical.
 */
const EXPORT_MENU_PANEL_CLASS = clsx(
  'z-[100] w-52 origin-top-left rounded-md bg-white p-1 shadow-lg ring-1 ring-black/5',
  'focus:outline-none',
  'transition data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0',
  'data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in',
  'dark:bg-neutral-900 dark:ring-white/10'
);

const EXPORT_MENU_HEADING_CLASS = clsx(
  'px-3 pt-2 pb-1 text-[11px] font-medium uppercase tracking-wide',
  'text-neutral-400 dark:text-neutral-500'
);

const EXPORT_MENU_ITEM_CLASS = clsx(
  'group flex w-full items-center gap-2.5 rounded px-3 py-2 text-left text-sm',
  'text-neutral-700 dark:text-neutral-200',
  'data-[focus]:bg-neutral-100 data-[focus]:text-neutral-900',
  'dark:data-[focus]:bg-neutral-800 dark:data-[focus]:text-white',
  'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50'
);

const EXPORT_MENU_ITEM_ICON_CLASS = clsx(
  'size-4 shrink-0',
  'text-neutral-500 dark:text-neutral-400',
  'group-data-[focus]:text-neutral-700 dark:group-data-[focus]:text-neutral-200'
);

const DOWNLOAD_OPTIONS: {
  format: CaraWorkspaceMessageExportFormat;
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
}[] = [
  { format: CaraWorkspaceMessageExportFormat.Docx, label: 'Word', Icon: DocumentTextIcon },
  { format: CaraWorkspaceMessageExportFormat.Xlsx, label: 'Excel', Icon: TableCellsIcon },
  { format: CaraWorkspaceMessageExportFormat.Pdf, label: 'PDF', Icon: PdfIcon }
];

const MessageExportMenu = (props: {
  disabled: boolean;
  onExport: (format: CaraWorkspaceMessageExportFormat) => void;
  triggerClassName: string;
  tooltipClassName: string;
}) => (
  <Headless.Menu as="div" className="relative inline-block">
    <Headless.MenuButton
      as="button"
      type="button"
      disabled={props.disabled}
      aria-label="Download"
      aria-busy={props.disabled}
      className={props.triggerClassName}
    >
      {props.disabled ? (
        <span aria-hidden>
          <Spinner size="size-4" color="neutral" />
        </span>
      ) : (
        <ArrowDownTrayIcon className="size-4" aria-hidden />
      )}
      <span className={clsx(props.tooltipClassName, 'group-data-[open]:opacity-0')}>Download</span>
    </Headless.MenuButton>
    <Headless.MenuItems
      transition
      anchor={{ to: 'bottom start', gap: 8 }}
      className={EXPORT_MENU_PANEL_CLASS}
    >
      <Headless.MenuSection>
        <Headless.MenuHeading className={EXPORT_MENU_HEADING_CLASS}>Download</Headless.MenuHeading>
        {DOWNLOAD_OPTIONS.map(({ format, label, Icon }) => (
          <Headless.MenuItem key={format} disabled={props.disabled}>
            <button
              type="button"
              className={EXPORT_MENU_ITEM_CLASS}
              disabled={props.disabled}
              onClick={() => props.onExport(format)}
            >
              <span aria-hidden>
                <Icon className={EXPORT_MENU_ITEM_ICON_CLASS} />
              </span>
              <span>{label}</span>
            </button>
          </Headless.MenuItem>
        ))}
      </Headless.MenuSection>
    </Headless.MenuItems>
  </Headless.Menu>
);

// Reconciliation is the only destination, so without it the menu would open empty.
const SEND_UNAVAILABLE_TOOLTIP = 'Sending requires an active Applied Epic integration';

const MessageSendMenu = (props: {
  amsReconciliationEnabled?: boolean;
  onReconcile: () => void;
  triggerClassName: string;
  tooltipClassName: string;
}) => (
  <Headless.Menu as="div" className="relative inline-block">
    <Headless.MenuButton
      as="button"
      type="button"
      disabled={!props.amsReconciliationEnabled}
      aria-label="Send"
      className={props.triggerClassName}
    >
      <ArrowUpTrayIcon className="size-4" aria-hidden />
      <span className={clsx(props.tooltipClassName, 'group-data-[open]:opacity-0')}>
        {props.amsReconciliationEnabled ? 'Send' : SEND_UNAVAILABLE_TOOLTIP}
      </span>
    </Headless.MenuButton>
    <Headless.MenuItems
      transition
      anchor={{ to: 'bottom start', gap: 8 }}
      className={EXPORT_MENU_PANEL_CLASS}
    >
      <Headless.MenuSection>
        <Headless.MenuHeading className={EXPORT_MENU_HEADING_CLASS}>Send</Headless.MenuHeading>
        {props.amsReconciliationEnabled && (
          <Headless.MenuItem>
            <button type="button" className={EXPORT_MENU_ITEM_CLASS} onClick={props.onReconcile}>
              <span aria-hidden>
                <CircleStackIcon className={EXPORT_MENU_ITEM_ICON_CLASS} />
              </span>
              <span>Reconcile with AMS</span>
            </button>
          </Headless.MenuItem>
        )}
      </Headless.MenuSection>
    </Headless.MenuItems>
  </Headless.Menu>
);

const MessageActions = (props: {
  message: CaraWorkspaceFieldsFragment['messages'][0];
  workspaceId: string;
  platformUnitsUsed?: number | null;
  amsReconciliationEnabled?: boolean;
}) => {
  const canViewUsageTab = useCanViewSettingsUsageTab();
  const [copied, setCopied] = React.useState(false);
  const copyText = async () => {
    const text = await remark()
      .use(stripMarkdown, {
        keep: ['list', 'listItem']
      })
      .process(props.message.content);
    navigator.clipboard.writeText(text.toString());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const exportHook = useExportCaraWorkspaceMessage({
    workspaceId: props.workspaceId,
    messageId: props.message.id
  });
  const isExporting = exportHook.exportingFormat != null;
  const [reconciling, setReconciling] = React.useState(false);

  const feedbackReasons: Record<MessageFeedbackNegativeReason, string> = {
    [MessageFeedbackNegativeReason.Misunderstanding]: "Didn't understand what I was asking",
    [MessageFeedbackNegativeReason.WrongInformation]: 'Wrong information in the form or response',
    [MessageFeedbackNegativeReason.MissingInformation]: 'Missing important information',
    [MessageFeedbackNegativeReason.NotHelpful]: 'Not helpful',
    [MessageFeedbackNegativeReason.TooVerbose]: 'Too long or wordy',
    [MessageFeedbackNegativeReason.TooSlow]: 'Too slow',
    [MessageFeedbackNegativeReason.Other]: 'Other reason'
  };

  const feedback = useFeedbackDialog<MessageFeedbackNegativeReason>({
    onSubmit: async ({ reasons, comment }) => {
      const payload: SubmitMessageRatingInput = {
        workspaceId: props.workspaceId,
        messageId: props.message.id,
        rating: MessageRating.Down,
        reasons,
        ...(comment ? { comment } : {})
      };
      await submitMessageRating(payload);
    },
    onSubmitPositive: async () => {
      await submitMessageRating({
        workspaceId: props.workspaceId,
        messageId: props.message.id,
        rating: MessageRating.Up,
        reasons: []
      });
    },
    otherReasonValue: MessageFeedbackNegativeReason.Other,
    resetOnContentChange: true,
    content: props.message.content
  });

  const actionButtonClass = (active: boolean) =>
    clsx(
      'p-1 rounded-md transition-all duration-150 relative group',
      // Dimmed by color rather than opacity: opacity would fade the tooltip inside the
      // button and trap its z-index beneath the follow-up options.
      'disabled:text-neutral-300 dark:disabled:text-neutral-600 disabled:hover:bg-transparent disabled:hover:text-neutral-300 dark:disabled:hover:text-neutral-600',
      active
        ? 'text-neutral-700 dark:text-neutral-300'
        : 'text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 dark:hover:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-white/5'
    );

  const tooltipClass =
    'absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2 py-1 text-xs text-white bg-neutral-900 rounded opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50';

  return (
    <AnimateIn className="mt-3 flex flex-row items-center gap-2">
      <FeedbackDialog
        open={feedback.showFeedbackDialog}
        onClose={feedback.handleFeedbackCancel}
        reasons={feedbackReasons}
        selectedReasons={feedback.selectedReasons}
        feedbackComment={feedback.feedbackComment}
        isSubmitting={feedback.isSubmitting}
        submitError={feedback.submitError}
        isSubmitDisabled={feedback.isSubmitDisabled}
        onSubmit={feedback.handleFeedbackSubmit}
        onCancel={feedback.handleFeedbackCancel}
        onToggleReason={feedback.toggleReason}
        onCommentChange={feedback.setFeedbackComment}
        onClearError={() => feedback.setSubmitError(null)}
      />
      <Headless.Button
        type="button"
        onClick={() => copyText()}
        className={actionButtonClass(copied)}
        aria-label="Copy to clipboard"
      >
        {copied ? <CheckIcon className="size-4" /> : <CopyIcon className="size-4" />}
        <span className={tooltipClass}>{copied ? 'Copied to clipboard' : 'Copy to clipboard'}</span>
      </Headless.Button>
      <MessageExportMenu
        disabled={isExporting}
        onExport={exportHook.exportMessage}
        triggerClassName={actionButtonClass(false)}
        tooltipClassName={tooltipClass}
      />
      <MessageSendMenu
        amsReconciliationEnabled={props.amsReconciliationEnabled}
        onReconcile={() => setReconciling(true)}
        triggerClassName={actionButtonClass(false)}
        tooltipClassName={tooltipClass}
      />
      <AmsReconciliationModal
        open={reconciling}
        onClose={() => setReconciling(false)}
        source={{ workspaceId: props.workspaceId, messageId: props.message.id }}
        workspaceId={props.workspaceId}
      />
      <Headless.Button
        type="button"
        onClick={() => feedback.handleThumbClick(MessageRating.Up)}
        className={actionButtonClass(feedback.rating === MessageRating.Up)}
        aria-label="Good response"
        aria-pressed={feedback.rating === MessageRating.Up}
        disabled={feedback.isSubmitting}
      >
        <ThumbsUpIcon className="w-4 h-4" filled={feedback.rating === MessageRating.Up} />
        <span className={tooltipClass}>Good response</span>
      </Headless.Button>
      <Headless.Button
        type="button"
        onClick={() => feedback.handleThumbClick(MessageRating.Down)}
        className={actionButtonClass(feedback.rating === MessageRating.Down)}
        aria-label="Bad response"
        aria-pressed={feedback.rating === MessageRating.Down}
        disabled={feedback.isSubmitting}
      >
        <ThumbsDownIcon className="w-4 h-4" filled={feedback.rating === MessageRating.Down} />
        <span className={tooltipClass}>Bad response</span>
      </Headless.Button>
      {canViewUsageTab && props.platformUnitsUsed != null && (
        <div
          className="p-1 rounded-md text-neutral-400 dark:text-neutral-500 relative group"
          aria-label={`${formatUsageUnits(props.platformUnitsUsed)} platform units used`}
        >
          <InfoIcon className="w-4 h-4" />
          <span className={tooltipClass}>
            {formatUsageUnits(props.platformUnitsUsed)} platform units used
          </span>
        </div>
      )}
      {exportHook.exportError && (
        <p role="alert" aria-live="polite" className="text-xs text-rose-600 dark:text-rose-400">
          {exportHook.exportError}
        </p>
      )}
      {!feedback.showFeedbackDialog && (
        <div>
          {feedback.isSubmitting && !feedback.submitError && (
            <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">Saving feedback…</p>
          )}
          {feedback.submitError && (
            <p className="mt-2 text-xs text-rose-600 dark:text-rose-400">{feedback.submitError}</p>
          )}
          {!feedback.submitError && feedback.rating && !feedback.isSubmitting && (
            <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
              Thanks for letting us know!
            </p>
          )}
        </div>
      )}
    </AnimateIn>
  );
};
